"use client";

import { useEffect } from "react";
import { toast } from "sonner";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useMutation, useQuery } from "@apollo/client";
import { SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { SheetScrollableContent } from "@/components/ui/sheet-scrollable-content";
import LanguageSwitcher from "@/components/shared/languageSelect";
import { useLanguage } from "@/context/LanguageContext";
import "@/components/styles/Form.css";
import InputFieldContainer from "@/components/shared/inputComponents/InputFieldContainer";
import {
  GET_SIMPLE_PRODUCT_DETAILS,
  UPDATE_SIMPLE_PRODUCT_PROFILES,
} from "../productQueries";

const formSchema = z.object({
  product_profiles: z
    .array(
      z.object({
        id: z.number().optional(),
        name: z.string().min(1, "Name is required"),
        description: z.string().optional(),
        language: z.string(),
      })
    )
    .transform((items, ctx) => {
      const filteredItems = items.filter((item) => item.name?.trim() !== "");
      if (filteredItems.length === 0) {
        items.forEach((item, index) => {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "At least one language profile needs to be filled",
            path: [`[${index}].name`],
          });
        });
      }
      return filteredItems;
    }),
});

interface SimpleProductFormProps {
  data: {
    id: string;
    type: string;
  };
  onDirtyChange?: (dirty: boolean) => void;
  onRequestClose?: () => void;
}

export default function SimpleProductForm({
  data,
  onDirtyChange,
  onRequestClose,
}: SimpleProductFormProps) {
  const { allLanguages, languageId } = useLanguage();
  const id = data?.id;

  const {
    data: { findUniqueProductContainer: productData } = {},
    loading: loadingProductData,
  } = useQuery(GET_SIMPLE_PRODUCT_DETAILS, {
    variables: {
      id,
    },
    fetchPolicy: "network-only",
    skip: !id,
  });

  const form = useForm({
    resolver: zodResolver(formSchema),
    mode: "onSubmit",
    defaultValues: {
      product_profiles: allLanguages.map((lang) => ({
        id: undefined,
        name: "",
        description: "",
        language: lang,
      })),
    },
  });

  const [updateProduct] = useMutation(UPDATE_SIMPLE_PRODUCT_PROFILES, {
    onError: (e) => {
      toast.error("Failed to update product");
    },
    onCompleted: () => {
      toast.success("Product updated successfully");
      onRequestClose?.();
    },
    refetchQueries: ["FindManyProductContainer"],
  });

  function onSubmit(values: z.infer<typeof formSchema>) {
    updateProduct({
      variables: {
        id,
        product_profiles: values.product_profiles.map((p) => ({
          id: p.id,
          name: p.name,
          description: p.description,
          language: p.language,
        })),
      },
    });
  }

  useEffect(() => {
    if (productData?.single_product?.product_profiles) {
      form.reset({
        product_profiles: productData.single_product.product_profiles
          .map((p) => ({
            id: p.id,
            name: p.name,
            description: p.description ?? "",
            language: p.language,
          }))
          .sort(
            (a, b) =>
              allLanguages.indexOf(a.language) -
              allLanguages.indexOf(b.language)
          ),
      });
    }
  }, [productData, form.reset, allLanguages]);

  useEffect(() => {
    if (onDirtyChange) {
      onDirtyChange(form.formState.isDirty);
    }
  }, [form.formState.isDirty, onDirtyChange]);

  console.log("productData", productData);

  return (
    <SheetContent className="sheet-content flex flex-col">
      <SheetHeader className="flex-shrink-0">
        <div className="flex justify-between items-center">
          <SheetTitle>Simple Product</SheetTitle>
          <LanguageSwitcher />
        </div>
      </SheetHeader>
      <SheetScrollableContent>
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="space-y-4 max-w-3xl mx-auto py-5"
          >
            <div className="grid gap-4 py-4">
              <InputFieldContainer
                label="Product Name"
                description="The name of your product that will be displayed to customers."
                control={form.control}
                name={`product_profiles.${languageId}.name`}
                render={({ field }) => (
                  <FormItem className="col-span-3">
                    <FormControl>
                      <Input placeholder="Enter product name" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <InputFieldContainer
                label="Description"
                description="Detailed description of your product including key features and benefits."
                control={form.control}
                name={`product_profiles.${languageId}.description`}
                render={({ field }) => (
                  <FormItem className="col-span-3">
                    <FormControl>
                      <Textarea
                        placeholder="Enter product description"
                        className="min-h-[100px] resize-none"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="flex justify-end gap-2">
                <Button type="submit" disabled={loadingProductData}>
                  Save Changes
                </Button>
              </div>
            </div>
          </form>
        </Form>
      </SheetScrollableContent>
    </SheetContent>
  );
}
